"use client";

import { motion } from "framer-motion";
import { Icon } from "@/components/ui/Icon";
import Image, { type StaticImageData } from "next/image";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";
import imageAeroport from "@/assets/images/chauffeur/aeroport/Transferts-aeroports-heliports.png";
import imageAeroportNice from "@/assets/images/chauffeur/aeroport/liaisons/Aeroport-Nice.png";
import imageAeroportMarseille from "@/assets/images/chauffeur/aeroport/liaisons/Aeroport-Marseille.png";
import imageAeroportToulon from "@/assets/images/chauffeur/aeroport/liaisons/Aeroport-Toulon.png";
import imageAeroportMilan from "@/assets/images/chauffeur/aeroport/liaisons/Aeroport-Milan-Malpensa.png";
import imageHeliportMonaco from "@/assets/images/chauffeur/aeroport/liaisons/Heliport-Monaco.png";
import imageCongresSalons from "@/assets/images/chauffeur/events/usage/Congres-salons.png";
import imageMariages from "@/assets/images/chauffeur/events/usage/Mariages.png";
import imageMiseADisposition from "@/assets/images/chauffeur/entreprise/Services/Mise-a-disposition.png";
import imageNavettesEquipes from "@/assets/images/chauffeur/entreprise/Services/Navettes-equipes.png";
import imageServicesMiseADisposition from "@/assets/images/chauffeur/services/services/Mise-a-disposition.png";
import imageTransfertsAeroports from "@/assets/images/chauffeur/services/services/Transferts-aeroports.png";
import imageServicesEvenements from "@/assets/images/chauffeur/services/services/evenements.png";
import imageServicesBusiness from "@/assets/images/chauffeur/services/services/Business.png";
import imageGareDeNice from "@/assets/images/chauffeur/transfert/services/Gare-de-Nice.png";
import imageNavettesEvenementsCannes from "@/assets/images/chauffeur/cannes/services/Navettes-evenements.png";
import imageMiseADispositionCannes from "@/assets/images/chauffeur/cannes/services/Mise-a-disposition.png";
import imageTransfertsCoteAzur from "@/assets/images/chauffeur/cote-azur/services/Transferts.png";
import imageEvenementsCoteAzur from "@/assets/images/chauffeur/cote-azur/services/evenements.png";
import imageComfort from "@/assets/images/chauffeur/Ambiance-interieur-premium.png";
import imageService from "@/assets/images/chauffeur/services/Experience-passager.png";
import imageAccueil from "@/assets/images/chauffeur/Prise-en-charge-aeroport-hotel.png";

type Service = { title: string; text: string; icon?: string };

type ServicesProps = {
  heading: string;
  services: Service[];
  variant?: "dark" | "light";
  locale?: Locale;
};

const resolveServiceImage = (
  title: string,
  sectionHeading: string,
  index: number,
): StaticImageData => {
  const label = title.toLowerCase();
  const heading = sectionHeading.toLowerCase();
  if (heading.includes("nos services vtc")) {
    if (
      label.includes("mise à disposition") ||
      label.includes("mise a disposition")
    ) {
      return imageServicesMiseADisposition;
    }
    if (
      label.includes("transferts aéroports") ||
      label.includes("transferts aeroports")
    ) {
      return imageTransfertsAeroports;
    }
    if (label.includes("événements") || label.includes("evenements")) {
      return imageServicesEvenements;
    }
    if (label.includes("business")) {
      return imageServicesBusiness;
    }
  }
  if (heading.includes("services sur cannes")) {
    if (
      label.includes("navettes événements") ||
      label.includes("navettes evenements")
    ) {
      return imageNavettesEvenementsCannes;
    }
    if (
      label.includes("mise à disposition") ||
      label.includes("mise a disposition")
    ) {
      return imageMiseADispositionCannes;
    }
  }
  if (
    heading.includes("prestations sur la côte d’azur") ||
    heading.includes("prestations sur la côte d'azur")
  ) {
    if (label.includes("transferts")) {
      return imageTransfertsCoteAzur;
    }
    if (label.includes("événements") || label.includes("evenements")) {
      return imageEvenementsCoteAzur;
    }
  }
  if (
    (label.includes("aéroport") || label.includes("aeroport")) &&
    label.includes("nice")
  ) {
    // Tolère "aéroport de Nice" / "aéroport Nice" → image Nice dédiée (sinon
    // retombe sur l'image aéroport générique et double celle des bénéfices).
    return imageAeroportNice;
  }
  if (
    label.includes("aéroport marseille") ||
    label.includes("aeroport marseille")
  ) {
    return imageAeroportMarseille;
  }
  if (label.includes("aéroport toulon") || label.includes("aeroport toulon")) {
    return imageAeroportToulon;
  }
  if (
    label.includes("aéroport milan malpensa") ||
    label.includes("aeroport milan malpensa")
  ) {
    return imageAeroportMilan;
  }
  if (label.includes("héliport monaco") || label.includes("heliport monaco")) {
    return imageHeliportMonaco;
  }
  if (
    label.includes("congrès & salons") ||
    label.includes("congres & salons")
  ) {
    return imageCongresSalons;
  }
  if (label.includes("mariages")) {
    return imageMariages;
  }
  if (label.includes("gare de nice")) {
    return imageGareDeNice;
  }
  if (
    label.includes("mise à disposition") ||
    label.includes("mise a disposition")
  ) {
    return imageMiseADisposition;
  }
  if (
    label.includes("navettes équipes") ||
    label.includes("navettes equipes")
  ) {
    return imageNavettesEquipes;
  }
  if (
    label.includes("aéroport") ||
    label.includes("aeroport") ||
    label.includes("héliport") ||
    label.includes("heliport") ||
    label.includes("liaison")
  ) {
    return imageAeroport;
  }
  if (
    label.includes("business") ||
    label.includes("corporate") ||
    label.includes("entreprise")
  ) {
    // Image business dédiée (pas imageComfort, déjà utilisée par FieldPreview).
    return imageServicesBusiness;
  }
  if (label.includes("événement") || label.includes("evenement")) {
    // Image événement dédiée (pas imageAccueil, déjà utilisée par FieldPreview).
    return imageServicesEvenements;
  }
  // Fallback : au lieu de renvoyer TOUJOURS la même image (services non matchés
  // → répétition visible sur les pages ville), on tourne sur un petit pool de
  // visuels génériques, choisi de façon déterministe par le titre du service.
  // Pool DISJOINT de FieldPreviewSection (Prise-en-charge/Ambiance) et du pool
  // Benefits → évite qu'une même image apparaisse dans 2 sections de la page.
  const FALLBACK_POOL = [
    imageService,
    imageServicesMiseADisposition,
    imageTransfertsAeroports,
  ];
  // Rotation par INDEX (pas par hash) → 2 services non matchés ne tombent
  // jamais sur la même image du pool dans la même page.
  return FALLBACK_POOL[index % FALLBACK_POOL.length];
};

// Pool de secours (images distinctes) pour le dédoublonnage intra-section :
// si 2 services résolvent la MÊME image (collision mot-clé ou pigeonhole), le
// 2e reçoit la prochaine image encore inutilisée → aucune répétition dans la
// grille des services.
const SERVICE_IMAGE_BACKUP: StaticImageData[] = [
  imageService,
  imageServicesMiseADisposition,
  imageTransfertsAeroports,
  imageMariages,
  imageCongresSalons,
  imageNavettesEquipes,
  imageServicesBusiness,
  imageServicesEvenements,
];

/** Garantit des images distinctes dans une section (remplace les doublons). */
const dedupeSectionImages = (
  resolved: StaticImageData[],
  backup: StaticImageData[],
): StaticImageData[] => {
  const used = new Set<string>();
  return resolved.map((img) => {
    if (!used.has(img.src)) {
      used.add(img.src);
      return img;
    }
    const alt = backup.find((b) => !used.has(b.src)) ?? img;
    used.add(alt.src);
    return alt;
  });
};

export const ServicesSection = ({
  heading,
  services,
  variant = "dark",
  locale = "fr",
}: ServicesProps): JSX.Element => {
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);
  const isLight = variant === "light";
  const sectionBg = isLight ? "bg-white text-black" : "bg-black text-white";
  const cardBg = isLight ? "bg-white" : "bg-neutral-900/40";
  const borderCol = isLight ? "border-black/10" : "border-white/10";
  const subText = isLight ? "text-black/70" : "text-white/75";
  const serviceImages = dedupeSectionImages(
    services.map((s, i) => resolveServiceImage(s.title, heading, i)),
    SERVICE_IMAGE_BACKUP,
  );
  return (
    <section className={`relative overflow-hidden py-12 ${sectionBg}`}>
      <div className="pointer-events-none absolute inset-0">
        <div
          className={`absolute right-[-10%] top-[-10%] h-64 w-64 rounded-full blur-[120px] ${isLight ? "bg-primary/20" : "bg-primary/10"}`}
        />
      </div>
      <div className="relative mx-auto max-w-[90rem] px-6 md:px-8">
        <p
          className={`mb-2 text-xs uppercase tracking-[0.18em] ${isLight ? "text-black/55" : "text-white/55"}`}
        >
          {ui.servicesEyebrow}
        </p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          viewport={{ once: true, margin: "-10%" }}
          className="mb-6 font-space-age text-xl tracking-[0.2em]"
        >
          {heading}
        </motion.h2>
        <div className="grid gap-4 md:grid-cols-2">
          {services.map((s, idx) => (
            <motion.article
              key={s.title}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              viewport={{ once: true, margin: "-10%" }}
              className={`group relative overflow-hidden rounded-2xl border ${borderCol} ${cardBg} p-5 transition-colors hover:border-primary/40`}
            >
              <div
                className={`absolute left-0 top-0 h-full w-1 ${isLight ? "bg-gradient-to-b from-primary/80 to-gold-600/70" : "bg-gradient-to-b from-gold-200/70 to-primary/80"}`}
              />
              <div className="mb-3 overflow-hidden rounded-xl border border-black/10">
                <div className="relative aspect-[21/9] w-full">
                  <Image
                    src={serviceImages[idx]}
                    alt={s.title}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>
              </div>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={`ms-2 inline-flex h-7 w-7 items-center justify-center rounded-full border ${isLight ? "border-black/15 bg-white" : "border-white/15 bg-black/60"} text-primary`}
                >
                  <Icon icon={s.icon || "mdi:steering"} className="h-4 w-4" />
                </span>
                <h3 className="text-sm font-semibold">{s.title}</h3>
              </div>
              <p className={`text-sm ${subText}`}>{s.text}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
